import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Me, getMe } from './api'

interface SessionValue {
  me: Me | null
  loading: boolean
  /** Re-ask the server who we are — after signing in, or connecting the channel. */
  refresh: () => Promise<void>
  offline: string | null
}

const Ctx = createContext<SessionValue>({
  me: null, loading: true, refresh: async () => {}, offline: null,
})

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)
  const [offline, setOffline] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setMe(await getMe())
      setOffline(null)
    } catch (e) {
      setOffline((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  // A signed-in session can end while the tab sits open — an admin signs you
  // out, or the cookie finally expires. Re-check whenever the tab is looked at
  // again, so we notice before someone records into a dead session.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh() }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [refresh])

  const value = useMemo(() => ({ me, loading, refresh, offline }), [me, loading, refresh, offline])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export const useSession = () => useContext(Ctx)
